import { ref } from 'vue';

import { createAuditLearnings } from '../../application/useCases/createAuditLearnings';
import {
  auditSessionActivityCommitLearning,
  auditSessionActivityDeleteLearning,
  auditSessionActivityUpdateLearning,
  getAuditLearnings,
  getClinicSessionNoteTemplates,
} from '../../infrastructure/api/auditApi';

/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearningDbRecord} AuditLearningDbRecord */
/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */
/** @typedef {import('../../domain/entities/AuditLearning.types').NoteTemplateOption} NoteTemplateOption */

// eslint-disable-next-line import/prefer-default-export
export function useAuditLearnings() {
  const isProcessing = ref(false);

  const learningsUseCase = createAuditLearnings(
    auditSessionActivityCommitLearning,
    getAuditLearnings,
    getClinicSessionNoteTemplates,
    auditSessionActivityUpdateLearning,
    auditSessionActivityDeleteLearning,
  );

  /*
   * A save runs the safety check with the modal still open, so it is slow enough for a second one to be
   * started before the first answers. Only the newest save may paint the modal: an earlier one that
   * resolves late describes text the manager has already moved on from, and its banner would contradict
   * what is on screen. The write itself is safe either way — the backend screens exactly the text it
   * stores, in the same call — so this guards the UI and nothing else.
   */
  let latestSaveToken = 0;

  /**
   * @returns {{ isCurrent: () => boolean }}
   */
  function startSave() {
    latestSaveToken += 1;
    const token = latestSaveToken;

    return { isCurrent: () => token === latestSaveToken };
  }

  /**
   * Discards whatever save is in flight, so its result no longer counts as current.
   */
  function abandonSaves() {
    latestSaveToken += 1;
  }

  /**
   * Commit a learning to the clinic's session note review learning repository
   * @param {string} clinicId
   * @param {string} learning
   * @param {{
   *   sessionActivityType: string,
   *   sessionNoteTemplateId?: string,
   * }} context
   * @param {string} sessionActivityAuditId
   */
  async function commitLearning(clinicId, learning, context, sessionActivityAuditId = null) {
    isProcessing.value = true;
    try {
      return await learningsUseCase.commitLearning(
        clinicId,
        learning,
        context,
        sessionActivityAuditId,
      );
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Load all audit learnings for a clinic.
   * @param {string} clinicId
   * @returns {Promise<AuditLearning[]>}
   */
  async function loadLearnings(clinicId) {
    isProcessing.value = true;
    try {
      return await learningsUseCase.getLearnings(clinicId);
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Load the clinic's session note templates for the scope selector.
   * @param {string} clinicId
   * @returns {Promise<NoteTemplateOption[]>}
   */
  async function loadSessionNoteTemplates(clinicId) {
    return learningsUseCase.getSessionNoteTemplates(clinicId);
  }

  /**
   * Update the text of an existing audit learning.
   * @param {string} clinicId
   * @param {string} learningId
   * @param {string} learning
   * @returns {Promise<void>}
   */
  async function editLearning(clinicId, learningId, learning) {
    isProcessing.value = true;
    try {
      await learningsUseCase.updateLearning(clinicId, learningId, learning);
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Create a manually authored learning, verified or parked as a draft.
   * @param {{
   *   clinicId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   *   saveAsDraft?: boolean,
   * }} params
   * @returns {Promise<{ isCurrent: boolean }>}
   */
  async function createLearning(params) {
    const save = startSave();

    isProcessing.value = true;
    try {
      await learningsUseCase.createLearning(params);

      return { isCurrent: save.isCurrent() };
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Park new wording for an active learning as a draft revision of it.
   * @param {{
   *   clinicId: string,
   *   parentId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   * }} params
   * @returns {Promise<{ isCurrent: boolean }>}
   */
  async function createDraftRevision(params) {
    const save = startSave();

    isProcessing.value = true;
    try {
      await learningsUseCase.createDraftRevision(params);

      return { isCurrent: save.isCurrent() };
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Save an edit made on the moderation page: text and scope together.
   * @param {{
   *   clinicId: string,
   *   learningId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   *   saveAsDraft?: boolean,
   * }} params
   * @returns {Promise<{ isCurrent: boolean, promoted: boolean }>}
   */
  async function saveLearning(params) {
    const save = startSave();

    isProcessing.value = true;
    try {
      const { promoted } = await learningsUseCase.saveLearningScope(params);

      return { isCurrent: save.isCurrent(), promoted };
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Delete an audit learning document.
   * @param {string} clinicId
   * @param {string} learningId
   * @returns {Promise<void>}
   */
  async function removeLearning(clinicId, learningId) {
    isProcessing.value = true;
    try {
      await learningsUseCase.deleteLearning(clinicId, learningId);
    } finally {
      isProcessing.value = false;
    }
  }

  return {
    isProcessing,
    abandonSaves,
    commitLearning,
    createDraftRevision,
    createLearning,
    editLearning,
    loadLearnings,
    loadSessionNoteTemplates,
    removeLearning,
    saveLearning,
  };
}
